import { FOCUSABLE_SELECTOR } from '../Shared/focusable';

const TRIGGER_SELECTOR = '#desktopMegaNavTrigger';
const PANEL_SELECTOR = '.megaNavPanel';
const FLYOUT_TRIGGER_SELECTOR = '.navFlyoutTrigger';
const CLOSE_BUTTON_SELECTOR = '.megaNavClose';
const CLOSE_DELAY_MS = 200;

function initDesktopMegaNav(): void {
    const trigger = document.querySelector<HTMLElement>(TRIGGER_SELECTOR);
    const panel = document.querySelector<HTMLElement>(PANEL_SELECTOR);
    if (!trigger || !panel) {
        return;
    }

    const flyoutTriggers = Array.from(panel.querySelectorAll<HTMLElement>(FLYOUT_TRIGGER_SELECTOR));

    let closeTimeout: number | undefined;
    // True when the panel was opened by an explicit click/tap (or keyboard toggle).
    // A pinned panel ignores hover-out auto-close; it only closes on a second
    // trigger click, an outside click, Escape, or the close button.
    let pinned = false;

    function openPanel(): void {
        window.clearTimeout(closeTimeout);
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
    }

    function closePanel(): void {
        pinned = false;
        window.clearTimeout(closeTimeout);
        panel.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
    }

    // Click/tap toggles a sticky "pinned" open state. Basing the toggle on `pinned`
    // (not the is-open class) avoids the focus→open then click→close double-fire, so
    // the first tap reliably opens even after `focus` has already revealed the panel.
    function togglePinned(): void {
        if (pinned) {
            closePanel();
        } else {
            pinned = true;
            openPanel();
        }
    }

    function scheduleClose(): void {
        if (pinned) return;
        window.clearTimeout(closeTimeout);
        closeTimeout = window.setTimeout(() => {
            if (pinned) return;
            if (trigger.matches(':hover') || panel.matches(':hover')) return;
            closePanel();
        }, CLOSE_DELAY_MS);
    }

    // Moves keyboard focus to the first focusable element inside the panel (the first
    // flyout tab). Used when the panel is opened via keyboard so focus lands inside it
    // regardless of DOM order — the quicklinks sit between the trigger and the panel.
    function focusFirstInPanel(): void {
        panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    }

    function flyoutContentFor(flyoutTrigger: HTMLElement): HTMLElement | null {
        const targetId = flyoutTrigger.getAttribute('aria-controls');
        return targetId ? document.getElementById(targetId) : null;
    }

    function openFlyoutItem(flyoutTrigger: HTMLElement): void {
        flyoutTrigger.setAttribute('aria-expanded', 'true');
        flyoutContentFor(flyoutTrigger)?.classList.remove('hidden');
    }

    function closeFlyoutItem(flyoutTrigger: HTMLElement): void {
        flyoutTrigger.setAttribute('aria-expanded', 'false');
        flyoutContentFor(flyoutTrigger)?.classList.add('hidden');
    }

    function toggleFlyoutItem(flyoutTrigger: HTMLElement): void {
        if (flyoutTrigger.getAttribute('aria-expanded') === 'true') return;
        flyoutTriggers.forEach(closeFlyoutItem);
        openFlyoutItem(flyoutTrigger);
    }

    trigger.addEventListener('mouseenter', openPanel);
    trigger.addEventListener('mouseleave', scheduleClose);
    trigger.addEventListener('click', (event: MouseEvent) => {
        event.preventDefault();
        const wasClosed = !panel.classList.contains('is-open');
        togglePinned();
        // Enter/Space activation reports detail === 0 — move focus into the panel so keyboard
        // users land inside it. Mouse clicks (detail >= 1) leave focus on the trigger button.
        if (wasClosed && event.detail === 0) {
            focusFirstInPanel();
        }
    });

    // Menu-button keyboard pattern: ArrowDown opens the panel and moves focus into it.
    // (We intentionally do NOT open on plain focus — that closed the panel again as soon
    // as the user tabbed forward onto the quicklinks.)
    trigger.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        if (!pinned) {
            pinned = true;
            openPanel();
        }
        focusFirstInPanel();
    });

    panel.addEventListener('mouseenter', () => window.clearTimeout(closeTimeout));
    panel.addEventListener('mouseleave', scheduleClose);

    // Shift+Tab from the first item returns focus to the trigger and closes, because the
    // quicklinks — not the trigger — precede the panel in the DOM.
    panel.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || !event.shiftKey) return;
        if (document.activeElement === panel.querySelector(FOCUSABLE_SELECTOR)) {
            event.preventDefault();
            closePanel();
            trigger.focus();
        }
    });

    document.addEventListener('focusin', (event: FocusEvent) => {
        const target = event.target as Node;
        if (trigger.contains(target) || panel.contains(target)) return;
        closePanel();
    });

    document.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as Node;
        if (!panel.classList.contains('is-open')) return;
        if (trigger.contains(target) || panel.contains(target)) return;
        closePanel();
    });

    document.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !panel.classList.contains('is-open')) return;
        closePanel();
        trigger.focus();
    });

    flyoutTriggers.forEach(flyoutTrigger => {
        flyoutTrigger.addEventListener('click', () => toggleFlyoutItem(flyoutTrigger));
    });

    panel.querySelector<HTMLElement>(CLOSE_BUTTON_SELECTOR)?.addEventListener('click', () => {
        closePanel();
        trigger.focus();
    });

    // Pre-open the flyout for the section the user is currently in (its trigger carries
    // `active`); fall back to the first flyout so the panel never opens without content.
    const initialFlyout = flyoutTriggers.find(t => t.classList.contains('active')) ?? flyoutTriggers[0];
    if (initialFlyout) {
        openFlyoutItem(initialFlyout);
    }
}

// Run once the markup exists. The bundle may execute before or after the header is
// parsed, so guard on readyState instead of assuming a load order (a bare
// DOMContentLoaded listener would miss the event if the script runs after it fired).
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDesktopMegaNav);
} else {
    initDesktopMegaNav();
}
